/**
 * In-run progression events for the HUD widgets (currencies row, loot feed). meta/inrunHud.ts
 * publishes: `begin` when a run starts (the purse before it), `kill` when a kill-reward pop is
 * released (same frame as its floating "+N gold", after the killing blow's number) and `levelUp`.
 * One HUD exists at a time, so this is a plain module-level hub.
 */
import type { NpcId } from '@keystone/sim';

export interface FeedStart {
  gold: number;
  shards: number;
  heroLevel: number;
}

export interface FeedKill {
  npcId: NpcId;
  xp: number;
  gold: number;
  boss: boolean;
}

export interface ProgressListener {
  begin?(start: FeedStart | null): void;
  kill?(k: FeedKill, now: number): void;
  levelUp?(level: number, now: number): void;
}

const listeners = new Set<ProgressListener>();
let lastStart: FeedStart | null = null;

export const progressFeed = {
  /** Subscribe; a listener added after `begin` gets the current start at once. */
  subscribe(l: ProgressListener): () => void {
    listeners.add(l);
    if (lastStart) l.begin?.(lastStart);
    return () => listeners.delete(l);
  },
  begin(start: FeedStart | null): void {
    lastStart = start;
    for (const l of listeners) l.begin?.(start);
  },
  kill(k: FeedKill, now: number): void {
    for (const l of listeners) l.kill?.(k, now);
  },
  levelUp(level: number, now: number): void {
    for (const l of listeners) l.levelUp?.(level, now);
  },
  /** Forget the run (HUD disposed). */
  end(): void {
    lastStart = null;
  },
};
